// PRCTASection.js
import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MessageCircle, FileText } from 'lucide-react';
import { CALENDLY_PR_CANADA } from '../../../constants';

const PRCTASection = () => {
  const handleBookConsultation = () => {
    if (CALENDLY_PR_CANADA) {
      window.open(CALENDLY_PR_CANADA, '_blank', 'noopener,noreferrer');
    }
  };

  const ctaOptions = [
    {
      icon: Calendar,
      title: 'Speak to an Adviser',
      description: 'Set up a free consultation to talk through which route fits and whether you qualify',
      action: 'Book a Time',
      onClick: handleBookConsultation
    },
    {
      icon: MessageCircle,
      title: 'Contact Us',
      description: 'Send your question over and someone on the team will come back to you',
      action: 'Get in Touch',
      link: '/contact'
    },
    {
      icon: FileText,
      title: 'View Pricing',
      description: 'Look through what our PR application support costs in full',
      action: 'See Prices',
      link: '/pricing'
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-primary to-secondary">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Main CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-white/90 text-base font-light max-w-2xl mx-auto mb-8">
            Our team will walk you through each stage of a Canadian permanent residency application, from the first assessment to the day it is filed.
          </p>
          <button
            onClick={handleBookConsultation}
            className="bg-white hover:bg-gray-100 text-primary font-normal text-base px-8 py-4 rounded-lg transition-colors inline-flex items-center gap-2"
          >
            <Calendar size={20} />
            Book a Free PR Consultation
          </button>
        </motion.div>

        {/* CTA Options Grid */}
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {ctaOptions.map((option, index) => {
            const Icon = option.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-6 hover:bg-white/20 transition-colors"
              >
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-4">
                  <Icon size={24} className="text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {option.title}
                </h3>
                <p className="text-white/80 text-sm font-light leading-relaxed mb-4">
                  {option.description}
                </p>
                {option.onClick ? (
                  <button
                    onClick={option.onClick}
                    className="text-white text-sm font-normal hover:underline"
                  >
                    {option.action} →
                  </button>
                ) : (
                  <a
                    href={option.link}
                    className="text-white text-sm font-normal hover:underline"
                  >
                    {option.action} →
                  </a>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 grid md:grid-cols-3 gap-6 text-center"
        >
          <div className="text-white">
            <p className="text-3xl font-semibold mb-2">1:1</p>
            <p className="text-white/80 text-sm font-light">Eligibility Review First</p>
          </div>
          <div className="text-white">
            <p className="text-3xl font-semibold mb-2">2014</p>
            <p className="text-white/80 text-sm font-light">Registered in England &amp; Wales Since</p>
          </div>
          <div className="text-white">
            <p className="text-3xl font-semibold mb-2">6-24</p>
            <p className="text-white/80 text-sm font-light">Months, Typical Processing</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PRCTASection;
